import React, { useRef } from 'react'
import { PaginaNode } from '@gql/graphql'
import { useTrans } from 'hooks'
import { Page } from 'components/organisms'
import styles from './StoreLocatorDetailTemplate.module.sass'
import { MailLine, PhoneLine } from '@components/icons'
import { Breadcrumbs, Chip, Tag, WContainer } from '@components/atoms'
import { getIcon } from '@utils/icons'
import { RivenditoreStoreLocatorNode } from '@gql/graphql-get'
import Map, { Marker } from 'react-map-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { handleStoreLocatorDetailsTracking } from '@utils/tracking'
import { useRouter } from 'next/router'

interface Props {
  pagina?: PaginaNode
  rivenditore?: RivenditoreStoreLocatorNode
}

const StoreLocatorDetailTemplate = (props: Props) => {
  const { pagina = undefined, rivenditore = undefined } = props

  const t = useTrans()
  const mapContainerRef = useRef()
  const lat = parseFloat(rivenditore?.latitudine.replace(',', '.'))
  const lng = parseFloat(rivenditore?.longitudine.replace(',', '.'))

  const router = useRouter()

  const details = [
    {
      label: t('Telefono'),
      icon: <PhoneLine />,
      value: rivenditore?.telefono ? (
        <a href={`tel:${rivenditore.telefono}`}>{rivenditore.telefono}</a>
      ) : undefined,
    },
    {
      label: t('Email'),
      icon: <MailLine />,
      value: rivenditore?.email ? (
        <a href={`mailto:${rivenditore.email}`}>{rivenditore.email}</a>
      ) : undefined,
    },
  ].filter((x) => !!x.value)
  const items = rivenditore.tags
  const servizi = [
    {
      pk: 1,
      icon: 'consulenza',
      name: t("Consulenza all'acquisto"),
    },
    {
      pk: 2,
      icon: 'servizio_tecnico',
      name: t('Servizio tecnico'),
    },
    {
      pk: 3,
      icon: 'assistenza',
      name: t('Assistenza'),
    },
    {
      pk: 4,
      icon: 'ricambi',
      name: t('Accessori e Ricambi Originali'),
    },
    {
      pk: 5,
      icon: 'abbigliamento',
      name: t('Abbigliamento protettivo'),
    },
  ]

  return (
    <Page pagina={pagina} className={`${styles.root}`}>
      <main>
        <div className={styles.breadcrumbs}>
          <WContainer>
            <Breadcrumbs breadcrumbs={pagina.breadcrumbs} showLast />
          </WContainer>
        </div>
        <WContainer>
          <div className={styles.layout}>
            <div className={styles.main}>
              <h1 className={styles.title}>{rivenditore.ragioneSociale}</h1>
              <div className={styles.lists}>
                <div className={styles.tags}>
                  {(rivenditore.tipi.includes('rivenditore') ||
                    rivenditore.tipi.includes('b2b')) && (
                    <Tag label={t('Punto vendita')} variant={'categoria'} size="sm" />
                  )}
                  {rivenditore.tipi.includes('assistenza') && rivenditore.tipi.length == 1 && (
                    <Tag label={t('Centro assistenza')} variant={'categoria'} size="sm" />
                  )}
                  {rivenditore.tipi.includes('rivenditore') && (
                    <Tag label={t('Vendita online')} variant="novita" size="sm" />
                  )}
                </div>
                {!!items.length && (
                  <div className={styles.list}>
                    {items.map((x) => (
                      <Chip label={t(x)} size="sm" />
                    ))}
                  </div>
                )}
              </div>
              <div className={styles.content}>
                {(!!rivenditore.indirizzo ||
                  !!rivenditore.cap ||
                  !!rivenditore.citta ||
                  !!rivenditore.provincia) && (
                  <div className={styles.indirizzo}>
                    {rivenditore.indirizzo}
                    {!!rivenditore.cap && (
                      <>
                        {rivenditore.indirizzo ? <>, </> : <></>} {rivenditore.cap}
                      </>
                    )}
                    {!!rivenditore.citta && (
                      <>
                        {rivenditore.indirizzo || rivenditore.cap ? <>, </> : <></>}
                        {rivenditore.citta}
                      </>
                    )}
                    {rivenditore.provincia ? <> ({rivenditore.provincia})</> : <></>}
                  </div>
                )}
                {!!details?.length && (
                  <div className={styles.details}>
                    {details.map((x) => (
                      <div className={styles.detailItem}>
                        <span className={styles.detailIcon} aria-label={x.label}>
                          {x.icon}
                        </span>
                        <span
                          className={styles.detailValue}
                          onClick={() => {
                            handleStoreLocatorDetailsTracking(
                              [
                                Number(rivenditore.longitudine.replace(',', '.')),
                                Number(rivenditore.latitudine.replace(',', '.')),
                              ],
                              rivenditore.ragioneSociale,
                              x.label === 'Telefono' ? 'phone' : 'mail',
                              router.asPath
                            )
                          }}
                        >
                          {x.value}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.map} ref={mapContainerRef}>
              <Map
                mapboxAccessToken={process.env.MAPBOX_ACCESS_TOKEN}
                initialViewState={{
                  longitude: lng,
                  latitude: lat,
                  zoom: 11,
                }}
                mapStyle="mapbox://styles/mapbox/streets-v11"
                interactive={false}
              >
                <Marker longitude={lng} latitude={lat} anchor="bottom">
                  <img src={`/${process.env.SITE}/markers/marker.png`} />
                </Marker>
              </Map>
            </div>
          </div>
        </WContainer>
        <div className={styles.services}>
          <WContainer>
            <div className={styles.servicesLayout}>
              <h2 className={styles.servicesTitle}>{t('I servizi')}</h2>
              <div className={styles.servicesList}>
                {servizi.map((s) => (
                  <div className={styles.servicesItem}>
                    <span className={styles.servicesItemIcon}>{getIcon(s.icon)}</span>
                    <span className={styles.servicesItemLabel}>{s.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </WContainer>
        </div>
      </main>
    </Page>
  )
}

export default StoreLocatorDetailTemplate
